import cornerstone from 'cornerstone-core';

let channel;
let channelId;
let ownsEnabledState = true;
let referenceLinesEnabled = false;
let remoteReference = null;
let selectedTool;
let localWindowId;
let activeViewport;
const registeredElements = new WeakSet();
const enabledElementsByIndex = new Map();

const createWindowId = () => (window.crypto?.randomUUID && window.crypto.randomUUID())
  || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

const dispatchActiveViewportChange = () => {
  window.dispatchEvent(new CustomEvent('viewer-active-viewport-changed', {
    detail: activeViewport ? { ...activeViewport } : null,
  }));
};

const toVector = (value) => {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return [value.x, value.y, value.z];
  return null;
};

const dot = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
const subtract = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
const cross = (a, b) => [
  a[1] * b[2] - a[2] * b[1],
  a[2] * b[0] - a[0] * b[2],
  a[0] * b[1] - a[1] * b[0],
];

const getPixelSpacing = (value) => {
  const spacing = Number(value);
  return Number.isFinite(spacing) && spacing > 0 ? spacing : 1;
};

const getPlane = (image) => {
  if (!image?.imageId) return null;
  const metadata = cornerstone.metaData.get('imagePlaneModule', image.imageId);
  if (!metadata?.frameOfReferenceUID) return null;
  const rowCosines = toVector(metadata.rowCosines);
  const columnCosines = toVector(metadata.columnCosines);
  const imagePositionPatient = toVector(metadata.imagePositionPatient);
  if (!rowCosines || !columnCosines || !imagePositionPatient) return null;
  return {
    frameOfReferenceUID: metadata.frameOfReferenceUID,
    rowCosines,
    columnCosines,
    imagePositionPatient,
    rowPixelSpacing: getPixelSpacing(metadata.rowPixelSpacing ?? metadata.pixelSpacing?.[0]),
    columnPixelSpacing: getPixelSpacing(metadata.columnPixelSpacing ?? metadata.pixelSpacing?.[1]),
    rows: metadata.rows || image.rows,
    columns: metadata.columns || image.columns,
  };
};

const broadcast = (message) => {
  if (channel) channel.postMessage({ ...message, channelId });
};

const redrawEnabledElements = () => {
  cornerstone.getEnabledElements().forEach(({ element }) => {
    try {
      if (cornerstone.getEnabledElement(element).image) cornerstone.updateImage(element);
    } catch (error) {
      // The viewport may have been removed while the message was being handled.
    }
  });
};

const getActivePlane = () => {
  if (!activeViewport || activeViewport.windowId !== localWindowId) return null;
  const element = enabledElementsByIndex.get(activeViewport.viewIndex);
  if (!element) return null;
  try {
    return getPlane(cornerstone.getEnabledElement(element).image);
  } catch (error) {
    return null;
  }
};

const publishActivePlane = () => {
  if (!activeViewport || activeViewport.windowId !== localWindowId) return;
  broadcast({
    type: 'reference-lines-plane',
    windowId: activeViewport.windowId,
    viewIndex: activeViewport.viewIndex,
    plane: getActivePlane(),
  });
};

const isValidPlane = (plane) => plane?.frameOfReferenceUID
  && plane.rowCosines?.length === 3
  && plane.columnCosines?.length === 3
  && plane.imagePositionPatient?.length === 3;

const projectToImage = (plane, point) => {
  const relativePoint = subtract(point, plane.imagePositionPatient);
  return {
    x: dot(relativePoint, plane.rowCosines) / plane.columnPixelSpacing,
    y: dot(relativePoint, plane.columnCosines) / plane.rowPixelSpacing,
  };
};

const clipLineToImage = (point, direction, columns, rows) => {
  let lower = -Infinity;
  let upper = Infinity;
  const bounds = [
    [-direction.x, point.x],
    [direction.x, columns - 1 - point.x],
    [-direction.y, point.y],
    [direction.y, rows - 1 - point.y],
  ];

  const intersects = bounds.every(([p, q]) => {
    if (p === 0) return q >= 0;
    const ratio = q / p;
    if (p < 0) lower = Math.max(lower, ratio);
    else upper = Math.min(upper, ratio);
    return lower <= upper;
  });
  if (!intersects) return null;

  return [
    { x: point.x + lower * direction.x, y: point.y + lower * direction.y },
    { x: point.x + upper * direction.x, y: point.y + upper * direction.y },
  ];
};

const calculateReferenceLine = (target, reference) => {
  if (!isValidPlane(target) || !isValidPlane(reference)
    || target.frameOfReferenceUID !== reference.frameOfReferenceUID) return null;

  const targetNormal = cross(target.rowCosines, target.columnCosines);
  const referenceNormal = cross(reference.rowCosines, reference.columnCosines);
  const direction = cross(targetNormal, referenceNormal);
  const denominator = dot(direction, direction);
  // Match Cornerstone's reference-lines tool threshold (about 30 degrees).
  if (denominator < 0.25) return null;

  const targetDistance = dot(targetNormal, target.imagePositionPatient);
  const referenceDistance = dot(referenceNormal, reference.imagePositionPatient);
  const targetCross = cross(referenceNormal, direction);
  const referenceCross = cross(direction, targetNormal);
  const point = targetCross.map((value, index) => (
    value * targetDistance + referenceCross[index] * referenceDistance
  ) / denominator);

  const projectedPoint = projectToImage(target, point);
  const projectedDirection = {
    x: dot(direction, target.rowCosines) / target.columnPixelSpacing,
    y: dot(direction, target.columnCosines) / target.rowPixelSpacing,
  };
  return clipLineToImage(projectedPoint, projectedDirection, target.columns, target.rows);
};

const renderRemoteReferenceLines = (event) => {
  if (!referenceLinesEnabled || !remoteReference?.plane || !activeViewport
    || remoteReference.windowId !== activeViewport.windowId
    || remoteReference.viewIndex !== activeViewport.viewIndex
    || activeViewport.windowId === localWindowId) return;
  const eventData = event.detail;
  const targetElement = event.currentTarget;
  let image;
  try {
    image = cornerstone.getEnabledElement(targetElement).image;
  } catch (error) {
    return;
  }
  const targetPlane = getPlane(image);
  if (!targetPlane || !eventData?.canvasContext?.canvas) return;

  const canvas = eventData.canvasContext.canvas;
  const context = canvas.getContext('2d');
  if (!context) return;
  context.save();
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.strokeStyle = '#00ff00';
  context.lineWidth = 1;
  const line = calculateReferenceLine(targetPlane, remoteReference.plane);
  if (line) {
    const start = cornerstone.pixelToCanvas(targetElement, line[0]);
    const end = cornerstone.pixelToCanvas(targetElement, line[1]);
    context.beginPath();
    context.moveTo(start.x, start.y);
    context.lineTo(end.x, end.y);
    context.stroke();
  }
  context.restore();
};

export const initializeCrossWindow2dReferenceLines = (id, isOwner = false) => {
  if (!id || typeof BroadcastChannel === 'undefined') return;
  localWindowId = localWindowId || createWindowId();
  if (channelId === id && channel) {
    ownsEnabledState = isOwner;
    return;
  }
  if (channel) channel.close();
  channelId = id;
  ownsEnabledState = isOwner;
  channel = new BroadcastChannel(`viewer-reference-lines-${id}`);
  channel.onmessage = ({ data }) => {
    if (data?.channelId !== channelId) return;
    if (data.type === 'request-reference-lines-state') {
      if (ownsEnabledState) {
        broadcast({ type: 'reference-lines-enabled', enabled: referenceLinesEnabled });
        if (selectedTool) broadcast({ type: 'parent-selected-tool', tool: selectedTool });
        if (activeViewport) broadcast({ type: 'active-viewport', ...activeViewport });
        publishActivePlane();
      }
    }
    if (data.type === 'reference-lines-enabled') {
      referenceLinesEnabled = Boolean(data.enabled);
      window.dispatchEvent(new CustomEvent('viewer-reference-lines-enabled-changed', {
        detail: { enabled: referenceLinesEnabled },
      }));
      redrawEnabledElements();
    }
    if (data.type === 'parent-selected-tool' && typeof data.tool === 'string') {
      selectedTool = data.tool;
      if (activeViewport) {
        activeViewport.currentTool = selectedTool;
        dispatchActiveViewportChange();
      }
      window.dispatchEvent(new CustomEvent('viewer-parent-selected-tool', {
        detail: { tool: selectedTool },
      }));
    }
    if (data.type === 'active-viewport' && typeof data.windowId === 'string'
      && Number.isInteger(data.viewIndex)) {
      const {
        windowId,
        viewIndex,
        renderType,
        mode,
        viewType,
        availableRenderers,
        currentTool,
        referenceLinesVisible,
      } = data;
      activeViewport = {
        windowId,
        viewIndex,
        renderType,
        mode,
        viewType,
        availableRenderers,
        currentTool,
        referenceLinesVisible,
      };
      remoteReference = null;
      dispatchActiveViewportChange();
      if (referenceLinesEnabled) redrawEnabledElements();
    }
    if (data.type === 'active-viewport-command'
      && typeof data.windowId === 'string'
      && Number.isInteger(data.viewIndex)
      && typeof data.toolName === 'string') {
      window.dispatchEvent(new CustomEvent('viewer-active-viewport-command', {
        detail: {
          windowId: data.windowId,
          viewIndex: data.viewIndex,
          toolName: data.toolName,
          commandOptions: data.commandOptions,
        },
      }));
    }
    if (data.type === 'reference-lines-plane'
      && typeof data.windowId === 'string'
      && Number.isInteger(data.viewIndex)
      && activeViewport?.windowId === data.windowId
      && activeViewport.viewIndex === data.viewIndex) {
      remoteReference = {
        windowId: data.windowId,
        viewIndex: data.viewIndex,
        plane: isValidPlane(data.plane) ? data.plane : null,
      };
      if (referenceLinesEnabled) redrawEnabledElements();
    }
  };
  broadcast({ type: 'request-reference-lines-state' });
};

export const registerCrossWindow2dReferenceLinesElement = (element, index) => {
  if (!element) return;
  if (Number.isInteger(index)) {
    enabledElementsByIndex.set(index, element);
  }
  if (registeredElements.has(element)) return;
  registeredElements.add(element);
  element.addEventListener('cornerstonenewimage', publishActivePlane);
  element.addEventListener('cornerstoneimagerendered', renderRemoteReferenceLines);
};

export const setCrossWindow2dReferenceLinesEnabled = (enabled) => {
  const nextEnabled = Boolean(enabled);
  const changed = nextEnabled !== referenceLinesEnabled;
  if (ownsEnabledState) {
    referenceLinesEnabled = nextEnabled;
    if (changed) broadcast({ type: 'reference-lines-enabled', enabled: referenceLinesEnabled });
  }
  if (changed) {
    if (referenceLinesEnabled) publishActivePlane();
    redrawEnabledElements();
  }
};

export const setCrossWindowSelectedTool = (tool) => {
  selectedTool = tool;
  if (activeViewport) {
    activeViewport.currentTool = tool;
    dispatchActiveViewportChange();
  }
  if (ownsEnabledState && tool) {
    broadcast({ type: 'parent-selected-tool', tool });
  }
};

export const createActiveViewportContext = (layout, viewIndex) => {
  const element = layout?.elements?.[viewIndex];
  const viewType = element?.initOptions?.viewType?.type;
  const mode = layout?.isMPRModeEnabled
    ? 'mpr'
    : layout?.is3DModeEnabled
      ? '3d'
      : viewType === 'mpr'
        ? 'mpr'
        : viewType === 'vr'
          ? '3d'
          : '2d';

  return {
    renderType: element?.renderType,
    mode,
    viewType,
    availableRenderers: element?.availableRenderers,
    currentTool: layout?.currentTool,
  };
};

export const setCrossWindowActiveViewport = (viewIndex, context = {}) => {
  if (!localWindowId || !Number.isInteger(viewIndex)) return;
  activeViewport = { windowId: localWindowId, viewIndex, ...context };
  remoteReference = null;
  broadcast({ type: 'active-viewport', ...activeViewport });
  dispatchActiveViewportChange();
  publishActivePlane();
  if (referenceLinesEnabled) redrawEnabledElements();
};

export const updateCrossWindowActiveViewportContext = (context = {}) => {
  if (!activeViewport || activeViewport.windowId !== localWindowId) return;
  activeViewport = { ...activeViewport, ...context };
  broadcast({ type: 'active-viewport', ...activeViewport });
  dispatchActiveViewportChange();
};

export const getCrossWindowActiveViewport = () => activeViewport;

export const getCrossWindow2dReferenceLinesEnabled = () => referenceLinesEnabled;

export const isCrossWindowViewportActive = (viewIndex) => {
  if (!activeViewport) return null;
  return activeViewport.windowId === localWindowId && activeViewport.viewIndex === viewIndex;
};

export const getCrossWindowWindowId = () => localWindowId;

export const executeOnActiveViewport = (toolName, fallbackViewIndex, commandOptions) => {
  if (activeViewport && activeViewport.windowId !== localWindowId) {
    broadcast({
      type: 'active-viewport-command',
      windowId: activeViewport.windowId,
      viewIndex: activeViewport.viewIndex,
      toolName,
      commandOptions,
    });
    return true;
  }

  const target = activeViewport || {
    windowId: localWindowId,
    viewIndex: fallbackViewIndex,
  };
  if (!Number.isInteger(target.viewIndex)) return false;
  window.dispatchEvent(new CustomEvent('viewer-active-viewport-command', {
    detail: { ...target, toolName, commandOptions },
  }));
  return true;
};

export const closeCrossWindow2dReferenceLines = () => {
  if (channel) channel.close();
  channel = undefined;
  channelId = undefined;
  remoteReference = null;
  referenceLinesEnabled = false;
  activeViewport = undefined;
  localWindowId = undefined;
  enabledElementsByIndex.clear();
};
